/* eslint-disable no-underscore-dangle */
/*
  this is used for PLP product cards & wishlist
*/
import React, { useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { connectHitInsights } from 'react-instantsearch-dom';

import FastlyPicture from '../../atoms/fastly-picture';
import Button from '../../atoms/button';
import Price, { PriceSale } from './product-card-price';
import ProductCardRatings from './product-card-ratings';
import QuickShop from './product-card-quickshop';
import WishlistButton, { WishlistShareButton } from './product-card-wishlist';
import Typography from '../../atoms/typography';

import { aa } from '../../utils/algolia';
import { useSearch } from '../../utils/context/search-provider';
import { logAmplitude } from '../../utils/amplitude';
import parseSlugFromProductUrl from '../../utils/parseSlugFromProductUrl';
import { EGROWTHBOOK_FEATURES_FLAGS } from '../../utils/growthbook-constants';
import handleOnImageError from '../../utils/handleOnImageError';
import getPromoMessages from '../../utils/getPromoMessages';
import { addProductToShareableList, removeProductFromShareableList } from '../../store/modules/lists/actions';
import GA4 from '../../utils/ga4DataLayer';
import { mapProductToGA4Item } from '../../utils/ga4ItemMapper';

const LAZY_SRC =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

const FASTLY_ON = process.env.NEXT_PUBLIC_FASTLY_ON === 'true';

const CardPrice = styled(Price)`
  width: 100%;
  justify-content: flex-start;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: ${({ theme }) => theme.modularScale.thirteen};
  font-weight: 400;
  margin-top: 0.4rem;
  > ${PriceSale} {
    justify-content: flex-start;
    margin-right: 0.8rem;
  }
  @media screen and (min-width: ${(props) => props.theme.breakpoint.medium}) {
    font-size: ${({ theme }) => theme.modularScale.fourteen};
  }
`;

const CardPromo = styled(Typography)`
  width: 100%;
  justify-content: flex-start;
  color: ${({ theme }) => theme.color.redError};
  line-height: 1.7rem;
  margin-top: 0.4rem;
`;

const Wishlist = styled(WishlistButton)``;

const CardTitle = styled.h2(() => ({
  lineHeight: 1.4,
  marginBottom: '0.4rem',
  width: '100%',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden'
}));

const CardBrand = styled.h2(() => ({
  lineHeight: 1.1,
  marginBottom: '0.4rem',
  textTransform: 'uppercase',
  width: '100%',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden'
}));

const CardBadges = styled.ul(() => ({
  display: 'flex',
  justifyContent: 'flex-start',
  listStyleType: 'disc',
  fontSize: '1.2rem',
  lineHeight: '1.4rem',
  gridGap: '0.8rem'
}));

const Badge = styled.li(() => ({
  paddingRight: '0.8rem',
  ':first-child': {
    listStyleType: 'none'
  }
}));

const Image = styled.img`
  left: 0;
  max-width: 100%;
  opacity: 1;
  position: absolute;
  top: 0;
  width: 100%;
  height: auto;
`;

const FILTERED_IMGWRAP_EMO_PROPS = new Set(['FASTLY_ON', 'showQuickShop']);
const FILTERED_CARD_EMO_PROPS = new Set(['isShareableActive', 'checked']);

const ImageWrapper = styled('figure', {
  shouldForwardProp: (prop) => !FILTERED_IMGWRAP_EMO_PROPS.has(prop)
})`
  margin-bottom: ${({ showQuickShop }) => (showQuickShop ? '3rem' : 0)};
  overflow: hidden;
  padding-top: 100%;
  position: relative;
  text-align: center;

  ${FASTLY_ON ? 'picture' : Image} + ${FASTLY_ON ? 'picture' : Image} {
    display: none;
    opacity: 0;
    pointer-events: none;
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoint.medium}) {
    ${(props) =>
      props.hasSideImage
        ? css`
            ${FASTLY_ON ? 'picture' : Image} {
              transition: opacity ${props.theme.animation.slow} ${props.theme.animation.easeOutQuad};
            }
            :hover {
              ${FASTLY_ON ? 'picture' : Image} {
                opacity: 0;

                + ${FASTLY_ON ? 'picture' : Image} {
                  display: block;
                  opacity: 1;
                }
              }
            }
          `
        : ''}
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoint.small}) {
    margin-bottom: ${({ showQuickShop }) => (showQuickShop ? '4rem' : 0)};
  }
`;

const QuickShopButton = styled(Button)(({ theme }) => ({
  margin: '0.5rem auto 0 auto',
  display: 'none',
  height: '3rem',
  lineHeight: 'calc(3rem - 6px)',
  opacity: 0,
  transition: `opacity ${theme.animation.slow} ${theme.animation.easeOutQuad}`,
  [`@media screen and (min-width: ${theme.breakpoint.medium})`]: {
    display: 'flex'
  }
}));

const CardAnchor = styled.a`
  text-decoration: none;
`;
const OutletBadgeWrapper = styled.div`
 color: ${({ theme }) => theme.color.redError};
border: 1px solid ${({ theme }) => theme.color.redError};
display:inline-grid;
`
const OutletBadgeText = styled.span`
padding: 4px 8px;
font-weight: 400;
font-size: 16px;
line-height: 14px;
`

const wishlistCardStyles = ({ checked, theme }) => ({
  // this function will only run if isShareableActive is active
  // checked can only happen when isShareableActive is active
  // sets product card border styles when selecting items for sharing wishlist
  border: checked ? `2px solid ${theme.color.bluePrimary}` : `1px solid ${theme.color.brandBorder}`,
  '&:nth-of-type(odd)': {
    // override specific border values from base border styles
    borderBottom: checked ? `2px solid ${theme.color.bluePrimary}` : `1px solid ${theme.color.brandBorder}`,
    borderRight: checked ? `2px solid ${theme.color.bluePrimary}` : `1px solid ${theme.color.brandBorder}`
  }
});

const Card = styled('article', {
  shouldForwardProp: (prop) => !FILTERED_CARD_EMO_PROPS.has(prop)
})(({ isShareableActive, checked, theme }) => ({
  position: 'relative',
  cursor: 'pointer',
  padding: !isShareableActive ? '3.2rem 2.25rem' : '0.8rem',
  header: {
    alignItems: 'center',
    color: theme.color.bluePrimary,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: theme.font.sans,
    fontSize: theme.modularScale.thirteen,
    fontWeight: 400,
    letterSpacing: 0.5,
    marginBottom: '1.6rem',
    marginTop: '1.6rem',
    textAlign: 'left',
    [CardBrand]: { order: 1 },
    [CardTitle]: { order: 2 },
    [CardPrice]: { order: 3 },
    [CardPromo]: { order: 4 }
  },
  '&.--pulse': {
    animation: `${theme.animations.pulse} ${theme.animation.default}`
  },
  '> a': {
    display: 'block',
    textDecoration: 'none'
  },
  borderBottom: `1px solid ${theme.color.lightGrey}`,
  '&:nth-of-type(odd)': {
    borderRight: `1px solid ${theme.color.lightGrey}`,
    [`@media screen and (min-width: ${theme.breakpoint.medium})`]: {
      borderRight: 'none'
    }
  },
  [`@media screen and (min-width: ${theme.breakpoint.medium})`]: {
    padding: '1.6rem',
    borderBottom: 'none',
    header: {
      fontSize: theme.modularScale.fourteen,
      letterSpacing: '0.1rem',
      lineHeight: '1.5rem'
    },
    ':hover': {
      '> a > button': { opacity: 1 },
      [Wishlist]: { opacity: 1 },
      [QuickShopButton]: { opacity: 1 }
    }
  },
  ...(isShareableActive && wishlistCardStyles({ isShareableActive, checked, theme }))
}));

const ProductCard = ({
  hit,
  idx,
  removeLoadingState,
  hitPageNumber,
  pageSlug,
  activeQuickShopID,
  setActiveQuickshopID,
  isWishlistEnabled,
  insights,
  sponsored,
  disableLazyload,
  meta,
  growthbookFeatureFlagValues
}) => {
  const { [EGROWTHBOOK_FEATURES_FLAGS.BAZAARVOICE_ENABLED]: bazaarvoiceEnabled } =
    growthbookFeatureFlagValues || {};
  
  // Default to true if flag is undefined (fallback to default behavior)
  const isBazaarvoiceEnabled = bazaarvoiceEnabled !== undefined ? bazaarvoiceEnabled : true;
  const ref = useRef();
  const router = useRouter();
  const dispatch = useDispatch();
  const [wishedProductsToShareStatus, wishedProductsToShare] = useSelector((state) => [
    state.lists.wishedProductsToShareStatus,
    state.lists.wishedProductsToShare
  ]);
  const { updateAlgoliaSearchParams, updateScrollPage, updateScrollSlug } = useSearch();
  const isProductShared = wishedProductsToShare.some((objectID) => hit.objectID === objectID);
  const isShareableActive = wishedProductsToShareStatus;

  const calculateQuickshopPosition = () => hit.__position - 24 * (hitPageNumber - 1);

  const handleShareProduct = (productId) => {
    /** There is a list in store called wishedProductsToShare
     * (list of the products to be copied as a link). In onClick event on the Card
     * is checked if the product there is in this list. If exists, so the Product will be removed
     * and unchecked, in another case will be added in the list and checked. isProductShared
     * is a variable that returns this verification on line 236.
     */
    if (isProductShared) {
      return dispatch(removeProductFromShareableList(productId));
    }
    return dispatch(addProductToShareableList(productId));
  };

  const onCardClick = (e) => {
    if (isShareableActive) {
      e.preventDefault();
      e.stopPropagation();
      return handleShareProduct(hit.objectID);
    }
    const params = {
      eventName: 'Product Clicked',
      objectID: hit.objectID,
      position: hit.__position,
      queryID: hit.__queryID
    };
    const { objectID, queryID } = params;
    const { target } = e;
    const isQuickShopButton = target.dataset.id === 'quickshop';

    updateAlgoliaSearchParams({ objectID, queryID });
    insights('clickedObjectIDsAfterSearch', params);

    const eventProps = {
      product: {
        ...hit,
        category: hit.product_type?.[0],
        sku: hit.manufacturer_id
      },
      index: hit.__position,
      module: 'plp'
    };

    if (isQuickShopButton) {
      e.preventDefault();
      logAmplitude('Clicked Product Quick View', eventProps);
      
      // Track GA4 product quick view
      const ga4Item = mapProductToGA4Item(hit, hit.__position - 1);
      GA4.viewProductQuickView({
        items: [ga4Item],
        currency: 'USD',
        value: parseFloat(hit.price) || 0
      });
      
      setActiveQuickshopID(+hit.objectID);
    } else {
      updateAlgoliaSearchParams({ objectID, queryID });
      insights('clickedObjectIDsAfterSearch', params);
      logAmplitude('Clicked Product Card', eventProps);

      // Add GA4 tracking
      const ga4Item = mapProductToGA4Item(hit, hit.__position - 1);
      
      // Determine list ID and name based on current page
      let itemListId = 'product_list';
      let itemListName = 'Product List';
      
      if (pageSlug?.includes('/lists/wishlist')) {
        itemListId = 'wishlist';
        itemListName = 'Wishlist';
      } else if (pageSlug?.includes('/search')) {
        itemListId = 'search_results';
        itemListName = 'Search Results';
      } else if (pageSlug?.includes('/brands/')) {
        itemListId = 'brand_page';
        itemListName = 'Brand Page';
      } else if (pageSlug?.includes('/category/')) {
        itemListId = 'category_page';
        itemListName = 'Category Page';
      }
      
      GA4.selectItem({
        item_list_id: itemListId,
        item_list_name: itemListName,
        items: [ga4Item]
      });

      // remove page query param  in case its value is 1
      const [path, queryParams] = pageSlug.split('?');
      const searchParams = new global.URLSearchParams(queryParams);
      if (searchParams.get('page') === '1') {
        searchParams.delete('page');
      }

      const newQueryParams = searchParams.toString();
      const newPageSlug = newQueryParams === '' ? path : `${path}?${newQueryParams}`;

      // to store product & page slug for scroll restoration
      updateScrollPage(newPageSlug);
      updateScrollSlug(hit.slug);

      // in the case that the user clicked on a product
      // that is on a different page number from
      // the current page number in the URL,
      // push the correct page number slug
      // next.js's router to have the correct history
      // for scroll restoration
      if (!pageSlug.includes('/lists/wishlist/shared') && router.asPath !== pageSlug) {
        router.push(newPageSlug, undefined, { shallow: true });
      }

      if ((e.ctrlKey || e.metaKey) && window) {
        /* as next/router does not support opening links in new tab,
        using JS default window object */
        e.preventDefault();
        e.stopPropagation();
        window.open(`/product/${hit.slug}`, '_blank');
        /* Preventing security vulnerability as mentioned in https://thecodest.co/blog/web-app-security-target-_blank-vulnerability */
        window.opener = null;
      } else {
        router.push(`/product/${hit.slug}`);
      }
    }
    return null;
  };

  const onQuickShopClose = () => {
    setActiveQuickshopID(-1);

    if (ref.current) {
      ref.current.scrollIntoView({ block: 'center' });
      ref.current.classList.add('--pulse');
      setTimeout(() => ref.current.classList.remove('--pulse'), 400);
    }
  };

  return (
    <>
      <Card data-test-id="plp-product-card" ref={ref} isShareableActive={isShareableActive} checked={isProductShared}>
        {router.asPath.includes('/lists/wishlist') && <WishlistShareButton product={hit} />}
        <Link href={parseSlugFromProductUrl(hit)} passHref>
          <CardAnchor data-productslug={hit.slug} onClick={onCardClick}>
            {FASTLY_ON ? (
              <ImageWrapper hasSideImage={hit.side_image}>
                <FastlyPicture
                  Image={Image}
                  src={hit.image || `${process.env.NEXT_PUBLIC_ASSET_HOST}/images/default-image.jpg`}
                  alt={hit.title}
                  onLoad={() => {
                    if (idx === 0) removeLoadingState(hitPageNumber);
                  }}
                  onError={handleOnImageError}
                />

                {hit.side_image && (
                  <FastlyPicture
                    Image={Image}
                    className={disableLazyload ? '' : 'lazyload'}
                    src={disableLazyload ? hit.side_image : LAZY_SRC}
                    data-src={hit.side_image}
                    alt={hit.title}
                    onLoad={(e) => {
                      if (e.target.src !== LAZY_SRC) {
                        e.target.parentElement.style.display = 'block';
                      }
                    }}
                    onError={handleOnImageError}
                  />
                )}
              </ImageWrapper>
            ) : (
              <ImageWrapper data-test-id="plp_product_image" hasSideImage={hit.side_image}>
                <Image
                  src={hit.image || `${process.env.NEXT_PUBLIC_ASSET_HOST}/images/default-image.jpg`}
                  alt={hit.title}
                  onLoad={() => {
                    if (idx === 0) removeLoadingState(hitPageNumber);
                  }}
                  onError={handleOnImageError}
                />

                {hit.side_image && (
                  <Image
                    className={disableLazyload ? '' : 'lazyload'}
                    src={disableLazyload ? hit.side_image : LAZY_SRC}
                    data-src={hit.side_image}
                    alt={hit.title}
                    onLoad={(e) => {
                      if (e.target.src !== LAZY_SRC) {
                        e.target.style.display = 'block';
                      }
                    }}
                    onError={handleOnImageError}
                  />
                )}
              </ImageWrapper>
            )}

            <QuickShopButton data-id="quickshop" aria-label={`open "${hit.title}", quick shop`} outline>
              Quick Shop
            </QuickShopButton>

            <header>
              <CardTitle data-test-id="plp_product_name">{hit.title}</CardTitle>
              {hit.brand && <CardBrand>{hit.brand}</CardBrand>}
              {/*
               * TODO: TEC-8758 - For BV we're currently using the slug instead of productId
               * because of a backend data issue with the product feed. If the BE fixes the
               * data issue, we will need to update data-bv-product-id to the correct value.
               */}
              {isBazaarvoiceEnabled && (
                <ProductCardRatings productId={hit.slug} />
              )}
              <CardPrice hit={hit} />
              {hit?.active_advertised_promotions && hit?.active_advertised_promotions.length > 0
                ? getPromoMessages(hit.active_advertised_promotions).map((activePromoObject) => (
                    <CardPromo element="h2" like="dec-5">
                      {activePromoObject.plp_promotional_text}
                    </CardPromo>
                  ))
                : null}
            </header>
            <CardBadges>
              {hit.outlet ? (
                <OutletBadgeWrapper>
                  <OutletBadgeText>Outlet</OutletBadgeText>
                </OutletBadgeWrapper>
              ) : (
                <Badge>{sponsored ? 'Sponsored' : hit.trends?.[0]}</Badge>
              )}
            </CardBadges>
          </CardAnchor>
        </Link>
        {isWishlistEnabled && <Wishlist product={hit} />}
      </Card>

      {activeQuickShopID === +hit.objectID && (
        <QuickShop
          slug={hit.slug}
          onQuickShopClose={onQuickShopClose}
          position={hit.__position}
          quickshopPosition={calculateQuickshopPosition()}
          insights={insights}
          meta={meta}
          growthbookFeatureFlagValues={growthbookFeatureFlagValues}
        />
      )}
    </>
  );
};

ProductCard.defaultProps = {
  isWishlistEnabled: true,
  idx: 0,
  removeLoadingState: () => {},
  sponsored: false,
  disableLazyload: false,
  meta: {},
  growthbookFeatureFlagValues: {},
};

ProductCard.propTypes = {
  hit: PropTypes.object.isRequired,
  pageSlug: PropTypes.string.isRequired,
  isWishlistEnabled: PropTypes.bool,
  activeQuickShopID: PropTypes.number.isRequired,
  setActiveQuickshopID: PropTypes.func.isRequired,
  insights: PropTypes.func.isRequired,
  hitPageNumber: PropTypes.number.isRequired,
  idx: PropTypes.number,
  removeLoadingState: PropTypes.func,
  sponsored: PropTypes.bool,
  disableLazyload: PropTypes.bool,
  meta: PropTypes.object,
  growthbookFeatureFlagValues: PropTypes.object,
};

export default connectHitInsights(aa)(ProductCard);
